import { useAuth } from "../contexts/AuthContext";
import { permissionsFactory } from "../utils/utils";
import Page403 from "./403";
import { useEffect, useState } from "react";
import { 
  Box, 
  Text, 
  Loader, 
  Paper, 
  Stack, 
  Title, 
  Button, 
  Modal, 
  ScrollArea,
  Badge,
  ActionIcon,
  Select
} from '@mantine/core';
import { IconUsers, IconPhone } from '@tabler/icons-react';
import { useDisclosure } from '@mantine/hooks';
import { apiInstance } from '../services/api_service';
import { toast } from 'react-toastify';

const ReenrollmentPhoneCalls = () => {
  const { permissions } = useAuth();
  const grantedPermissions = permissionsFactory(permissions);
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [opened, { open, close }] = useDisclosure(false);
  const [daysBack, setDaysBack] = useState('30');

  const fetchData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Use selected number of days
      const endDate = new Date();
      const startDate = new Date();
      let startDateStr, endDateStr;
      
      // If 1 day is selected, show only today
      // If more than 1 day, show the range from X days ago to today
      if (parseInt(daysBack) === 1) {
        // For 1 day, use today as both start and end date
        startDateStr = endDate.toISOString().split('T')[0];
        endDateStr = endDate.toISOString().split('T')[0];
      } else {
        // For multiple days, go back X days from today
        startDate.setDate(startDate.getDate() - parseInt(daysBack) + 1);
        startDateStr = startDate.toISOString().split('T')[0];
        endDateStr = endDate.toISOString().split('T')[0];
      }
      
      const response = await apiInstance.get(
        `/students/phone-call-logs-by-user/?start_date=${startDateStr}&end_date=${endDateStr}`
      );
      setData(response.data);
    } catch (error) {
      console.error('Error fetching phone call logs:', error);
      setError('Erro ao carregar dados das ligações');
      toast.error('Erro ao carregar dados das ligações');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [daysBack]);

  // Check if user has reenrollment management permission
  if (!grantedPermissions.canManageReenrollment) {
    return <Page403 />;
  }

  const handleViewStudents = (user) => {
    setSelectedUser(user);
    open();
  };


  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <Loader size="lg" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <Text color="red" size="lg" mb="md">
            {error}
          </Text>
          <Button onClick={fetchData} variant="outline">
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <Title order={1} mb="sm" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <IconPhone size={28} />
          Ligações de Rematrícula
        </Title>
        <Text color="dimmed" size="sm" mb="md">
          Acompanhe o andamento das ligações de rematrícula por usuário
        </Text>
        
        <Paper p="md" withBorder>
          <Stack gap="sm">
            <Text fw={500} size="sm">
              Período de consulta
            </Text>
            <Select
              label="Últimos dias"
              placeholder="Selecione o período"
              value={daysBack}
              onChange={setDaysBack}
              data={Array.from({ length: 30 }, (_, i) => ({
                value: (i + 1).toString(),
                label: `${i + 1} dia${i + 1 !== 1 ? 's' : ''}`
              }))}
              size="sm"
              w={{ base: '100%', sm: '200px' }}
            />
          </Stack>
        </Paper>
      </div>

      {data.length === 0 ? (
        <Paper p="xl" className="text-center">
          <Text color="dimmed" size="lg">
            Nenhum dado encontrado nos últimos {daysBack} dia{daysBack !== '1' ? 's' : ''}
          </Text>
        </Paper>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
          {data.map((user) => (
            <Paper key={user.id} p="md" shadow="sm" radius="md" withBorder>
              <Stack gap="sm">
                <Box>
                  <Text fw={600} size="lg">
                    {user.first_name} {user.last_name}
                  </Text>
                  <Text size="sm" color="dimmed">
                    Ligou para responsáveis de {user.students.length} aluno{user.students.length !== 1 ? 's' : ''}
                  </Text>
                </Box>
                
                <Button
                  variant="outline"
                  size="sm"
                  fullWidth
                  leftSection={<IconUsers size={16} />}
                  onClick={() => handleViewStudents(user)}
                >
                  Ver alunos
                </Button>
              </Stack>
            </Paper>
          ))}
        </div>
      )}

      {/* Modal for showing student details */}
      <Modal
        opened={opened}
        onClose={close}
        title={`Alunos de ${selectedUser?.first_name} ${selectedUser?.last_name}`}
        size="lg"
      >
        {selectedUser && (
          <ScrollArea h={400}>
            <Stack gap="sm">
              {selectedUser.students.map((student) => (
                <Paper key={student.id} p="sm" withBorder>
                  <Text fw={500}>{student.name}</Text>
                </Paper>
              ))}
            </Stack>
          </ScrollArea>
        )}
      </Modal>
    </div>
  );
};

export default ReenrollmentPhoneCalls;
